/**
 * The TEAM of the Digital Central (doc 25 section 7; owner Q-367): a function in a country (optionally a subset of its markets)
 * with the languages it speaks, the channels it answers, and its weekly hours.
 *
 * A team is country-scoped DATA, not code: the country code is an ISO 3166-1 alpha-2 value that comes from the registered
 * countries at runtime, its hours are local wall-clock windows resolved in each market's own IANA timezone, and the name a
 * person reads comes from the function's label in the vocabulary catalog (only an operator-written `nameOverride` is stored text).
 * Membership never widens an operator's grants (see `membership.ts`). Pure: instants and ids are passed in.
 */
import { normalizeWeeklyHours } from './hours.js';
/** The closed list of team functions (a vocabulary: `central_teams.function`). */
export const TEAM_FUNCTIONS = ['atendimento', 'financeiro', 'comercial', 'cadastro_prestadores', 'seguranca', 'supervisao'];
/** The channels a team can answer (`central_teams.channel`). */
export const TEAM_CHANNELS = ['whatsapp', 'web_widget', 'mobile_app', 'voice'];
export const TEAM_STATUSES = ['ACTIVE', 'ARCHIVED'];
/** The functions whose team is open 24 h and cannot be given business hours (a safety contact reaches a person at any hour). */
export const ALWAYS_OPEN_FUNCTIONS = ['seguranca'];
/** The functions that are not a contact queue: the supervisors' own team answers no channel. */
export const NO_CHANNEL_FUNCTIONS = ['supervisao'];
export const MAX_NAME_LENGTH = 80;
export const MAX_LANGUAGES = 12;
/** Stable reason codes of a refused team write (catalogued as `problem.central_team`). */
export const CENTRAL_TEAM_REFUSAL_CODES = [
    'TEAM_NOT_FOUND',
    'TEAM_FUNCTION_INVALID',
    'TEAM_COUNTRY_INVALID',
    'TEAM_COUNTRY_NOT_REGISTERED',
    'TEAM_MARKETS_INVALID',
    'TEAM_MARKET_NOT_IN_COUNTRY',
    'TEAM_LANGUAGES_INVALID',
    'TEAM_CHANNELS_INVALID',
    'TEAM_NAME_INVALID',
    'TEAM_ALWAYS_OPEN_REQUIRED',
    'TEAM_SCOPE_DUPLICATE',
    'TEAM_ARCHIVED',
    'TEAM_NOT_ARCHIVED',
    'TEAM_VERSION_CONFLICT',
    'TEAM_VERSION_REQUIRED',
    'TEAM_PATCH_EMPTY',
    'HOURS_WINDOW_INVALID',
    'HOURS_WINDOW_ORDER',
    'HOURS_WINDOWS_OVERLAP',
    'HOURS_REQUIRED',
    'HOURS_NOT_ALLOWED_ALWAYS_OPEN',
    'TEAM_MEMBER_ROLE_INVALID',
    'MEMBER_ALREADY_IN_TEAM',
    'MEMBER_NOT_IN_TEAM',
    'MEMBER_NOT_DESK_AGENT',
    'MEMBER_GRANT_DOES_NOT_REACH_TEAM',
    'MEMBER_OPERATOR_NOT_FOUND',
    'MEMBER_DIRECTORY_UNAVAILABLE',
];
const COUNTRY = /^[A-Z]{2}$/;
const MARKET = /^([a-z]{2}):[a-z0-9]+(?:-[a-z0-9]+)*$/;
const LANGUAGE = /^[a-z]{2,3}(?:-[A-Za-z0-9]{2,8}){0,3}$/;
export const isTeamFunction = (v) => typeof v === 'string' && TEAM_FUNCTIONS.includes(v);
export const isTeamChannel = (v) => typeof v === 'string' && TEAM_CHANNELS.includes(v);
export const isCountryCode = (v) => typeof v === 'string' && COUNTRY.test(v);
/** The key that makes two ACTIVE teams of one function and country the same scope: `''` = the whole country. */
export const scopeKeyOf = (marketIds) => marketIds.join(',');
/** Validate a market subset for a country: `cc:slug` ids of THAT country, sorted, de-duplicated. */
export function normalizeMarkets(countryCode, marketIds) {
    const prefix = countryCode.toLowerCase();
    const out = new Set();
    for (const m of marketIds) {
        if (typeof m !== 'string')
            return { ok: false, code: 'TEAM_MARKETS_INVALID' };
        const match = MARKET.exec(m);
        if (match === null)
            return { ok: false, code: 'TEAM_MARKETS_INVALID' };
        if (match[1] !== prefix)
            return { ok: false, code: 'TEAM_MARKET_NOT_IN_COUNTRY' };
        out.add(m);
    }
    return { ok: true, value: [...out].sort() };
}
/** Validate the languages: BCP-47 tags, de-duplicated in the order given, one to {@link MAX_LANGUAGES}. */
export function normalizeLanguages(languages) {
    const out = [];
    for (const l of languages) {
        if (typeof l !== 'string' || !LANGUAGE.test(l))
            return { ok: false, code: 'TEAM_LANGUAGES_INVALID' };
        if (!out.includes(l))
            out.push(l);
    }
    if (out.length === 0 || out.length > MAX_LANGUAGES)
        return { ok: false, code: 'TEAM_LANGUAGES_INVALID' };
    return { ok: true, value: out };
}
/** Validate the channels of a function: known, de-duplicated in catalog order; at least one except for a function that answers none. */
export function normalizeChannels(fn, channels) {
    const set = new Set();
    for (const c of channels) {
        if (!isTeamChannel(c))
            return { ok: false, code: 'TEAM_CHANNELS_INVALID' };
        set.add(c);
    }
    if (NO_CHANNEL_FUNCTIONS.includes(fn))
        return set.size === 0 ? { ok: true, value: [] } : { ok: false, code: 'TEAM_CHANNELS_INVALID' };
    if (set.size === 0)
        return { ok: false, code: 'TEAM_CHANNELS_INVALID' };
    return { ok: true, value: TEAM_CHANNELS.filter((c) => set.has(c)) };
}
/** An operator-written name: trimmed, 1 to {@link MAX_NAME_LENGTH} characters, no control characters. `null`/empty clears it. */
export function normalizeName(name) {
    if (name === null || name === undefined)
        return { ok: true, value: undefined };
    if (typeof name !== 'string')
        return { ok: false, code: 'TEAM_NAME_INVALID' };
    const trimmed = name.trim();
    if (trimmed === '')
        return { ok: true, value: undefined };
    // eslint-disable-next-line no-control-regex
    if (trimmed.length > MAX_NAME_LENGTH || /[\u0000-\u001f\u007f]/.test(trimmed))
        return { ok: false, code: 'TEAM_NAME_INVALID' };
    return { ok: true, value: trimmed };
}
/** The hours rule of a function: safety is always open and cannot be given windows; the others are as asked. */
function hoursFor(fn, alwaysOpen, weeklyHours) {
    const forced = ALWAYS_OPEN_FUNCTIONS.includes(fn);
    const open = alwaysOpen ?? forced;
    if (forced && !open)
        return { ok: false, code: 'TEAM_ALWAYS_OPEN_REQUIRED' };
    const hours = normalizeWeeklyHours(weeklyHours, open);
    if (!hours.ok)
        return { ok: false, code: hours.code };
    return { ok: true, value: { alwaysOpen: open, weeklyHours: hours.hours } };
}
/** Validate a new team and stamp it (version 1, ACTIVE). */
export function createTeam(input, stamp) {
    if (!isTeamFunction(input.function))
        return { ok: false, code: 'TEAM_FUNCTION_INVALID' };
    if (!isCountryCode(input.countryCode))
        return { ok: false, code: 'TEAM_COUNTRY_INVALID' };
    const markets = normalizeMarkets(input.countryCode, input.marketIds ?? []);
    if (!markets.ok)
        return markets;
    const languages = normalizeLanguages(input.languages);
    if (!languages.ok)
        return languages;
    const channels = normalizeChannels(input.function, input.channels);
    if (!channels.ok)
        return channels;
    const hours = hoursFor(input.function, input.alwaysOpen, input.weeklyHours ?? []);
    if (!hours.ok)
        return hours;
    const name = normalizeName(input.nameOverride);
    if (!name.ok)
        return name;
    return {
        ok: true,
        value: {
            teamId: stamp.teamId,
            function: input.function,
            countryCode: input.countryCode,
            marketIds: markets.value,
            languages: languages.value,
            weeklyHours: hours.value.weeklyHours,
            alwaysOpen: hours.value.alwaysOpen,
            channels: channels.value,
            status: 'ACTIVE',
            isDefault: stamp.isDefault === true,
            ...(name.value !== undefined ? { nameOverride: name.value } : {}),
            version: 1,
            createdAt: stamp.at,
            createdBy: stamp.actor,
            updatedAt: stamp.at,
            updatedBy: stamp.actor,
        },
    };
}
/** The team with its name override set, or removed when `name` is undefined. */
function withName(team, name) {
    const rest = { ...team };
    delete rest.nameOverride;
    return (name !== undefined ? { ...rest, nameOverride: name } : rest);
}
const sameList = (a, b) => a.length === b.length && a.every((x, i) => x === b[i]);
const sameHours = (a, b) => a.length === b.length && a.every((w, i) => w.day === b[i]?.day && w.start === b[i]?.start && w.end === b[i]?.end);
/**
 * Apply a patch: every supplied field is validated against the team's own function; the hours are re-validated whenever the
 * hours or the always-open flag are supplied. An archived team is read-only. Bumps `version` and stamps who and when.
 */
export function updateTeam(team, patch, stamp) {
    if (team.status === 'ARCHIVED')
        return { ok: false, code: 'TEAM_ARCHIVED' };
    const supplied = ['marketIds', 'languages', 'weeklyHours', 'alwaysOpen', 'channels', 'nameOverride'].some((k) => patch[k] !== undefined);
    if (!supplied)
        return { ok: false, code: 'TEAM_PATCH_EMPTY' };
    let next = team;
    const changed = [];
    if (patch.marketIds !== undefined) {
        const m = normalizeMarkets(team.countryCode, patch.marketIds);
        if (!m.ok)
            return m;
        if (!sameList(m.value, team.marketIds)) {
            next = { ...next, marketIds: m.value };
            changed.push('marketIds');
        }
    }
    if (patch.languages !== undefined) {
        const l = normalizeLanguages(patch.languages);
        if (!l.ok)
            return l;
        if (!sameList(l.value, team.languages)) {
            next = { ...next, languages: l.value };
            changed.push('languages');
        }
    }
    if (patch.channels !== undefined) {
        const c = normalizeChannels(team.function, patch.channels);
        if (!c.ok)
            return c;
        if (!sameList(c.value, team.channels)) {
            next = { ...next, channels: c.value };
            changed.push('channels');
        }
    }
    if (patch.weeklyHours !== undefined || patch.alwaysOpen !== undefined) {
        const open = patch.alwaysOpen ?? team.alwaysOpen;
        // Turning always-open OFF needs windows to be supplied; turning it ON takes none. Windows are optional only when they stay as they are.
        const windows = patch.weeklyHours ?? (open === team.alwaysOpen ? team.weeklyHours : []);
        const h = hoursFor(team.function, open, windows);
        if (!h.ok)
            return h;
        if (h.value.alwaysOpen !== team.alwaysOpen || !sameHours(h.value.weeklyHours, team.weeklyHours)) {
            next = { ...next, alwaysOpen: h.value.alwaysOpen, weeklyHours: h.value.weeklyHours };
            changed.push('hours');
        }
    }
    if (patch.nameOverride !== undefined) {
        const n = normalizeName(patch.nameOverride);
        if (!n.ok)
            return n;
        if (n.value !== team.nameOverride) {
            next = withName(next, n.value);
            changed.push('nameOverride');
        }
    }
    if (changed.length === 0)
        return { ok: true, value: { team, changed } };
    return { ok: true, value: { team: { ...next, version: team.version + 1, updatedAt: stamp.at, updatedBy: stamp.actor }, changed } };
}
/** Archive a team (read-only afterwards; its members are kept). */
export function archiveTeam(team, stamp) {
    if (team.status === 'ARCHIVED')
        return { ok: false, code: 'TEAM_ARCHIVED' };
    return { ok: true, value: { ...team, status: 'ARCHIVED', version: team.version + 1, updatedAt: stamp.at, updatedBy: stamp.actor } };
}
/** Bring an archived team back. */
export function restoreTeam(team, stamp) {
    if (team.status !== 'ARCHIVED')
        return { ok: false, code: 'TEAM_NOT_ARCHIVED' };
    return { ok: true, value: { ...team, status: 'ACTIVE', version: team.version + 1, updatedAt: stamp.at, updatedBy: stamp.actor } };
}
//# sourceMappingURL=team.js.map